"use client";

import { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import Loader from "../../../../components/loader";
import PendingSignsAccordion from "./PendingSignsAccordion";
import { toast } from "react-toastify";
import { toastOptObj } from "../../../../utils/constants";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { APIGetTBSOrderDetailsDDl, APIGetTbsOrderSolution, APISaveAssignLocations } from "../../../../service/api-service";
import { useParams } from "react-router-dom";
import { GetTBSLocationEntity, PendingSignLocation, SaveAssignLocationsReqEntity, TBSOrderSolutionEntity } from "../../../../interface/tbs-tracker/send-tbs";
import { Message } from "primereact/message";
import { TBSTypeEnumEntity } from "../../../../utils/enum";
interface Props {
    locations: GetTBSLocationEntity[],
    qty: number;
    /** step 1 material height */
    materialHeight?: number | string;
    /** step 1 TBS type: a skin TBS can select no more items than the quantity */
    typeOfTbs?: TBSTypeEnumEntity;
    onNext: () => void;
    onBack: () => void;
}

interface OrderOption {
    orderId: string;
    orderNumber: string;
    projectName: string;
}

interface PendingSignData {
    id: string;
    orderId: string;
    orderRefNumber: string;
    title: string;
    locations: PendingSignLocation[];
}

const AssignLocationsStep = ({
    locations,
    qty,
    materialHeight,
    typeOfTbs = TBSTypeEnumEntity.SKIN,
    onNext,
    onBack,
}: Props) => {
    // A location taller than the material height (step 1) can be selected only when a grid is selected on it.
    // Once the TBS of every skin of the location is generated there is nothing left to select, so no height error is shown for it.
    const isHeightRestricted = (item: TBSOrderSolutionEntity): boolean => {
        const material = Number(materialHeight);
        const hasSkinPending = !!item.TBSglassOneIn || !!item.TBSglassTwoOut || !!item.inside || !!item.outside;
        return materialHeight !== undefined && materialHeight !== "" &&
            Number.isFinite(material) && material > 0 &&
            typeof item.solutionHeight === "number" &&
            item.solutionHeight > material &&
            !item.hasGrid &&
            hasSkinPending;
    };

    const { id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isPendingSignSubmitted, setIsPendingSignSubmitted] = useState(false);
    const [isSaveSubmitted, setIsSaveSubmitted] = useState(false);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [pendingSigns, setPendingSigns] = useState<PendingSignData[]>([]);
    const [orderOptions, setOrderOptions] = useState<OrderOption[]>([]);
    const [selectedOrderOption, setSelectedOrderOption] = useState<OrderOption | null>(null);

    // inside + outside + handle each count as one item of the quantity
    const countSelected = (signs: PendingSignData[]): number => signs.reduce((count, order) =>
        count + order.locations.reduce((locationCount, location) =>
            locationCount + (location.inside ? 1 : 0) + (location.outside ? 1 : 0) + (location.handle ? 1 : 0), 0), 0);
    const selectedCount: number = countSelected(pendingSigns);
    const isSkinSelectionLimited: boolean = typeOfTbs !== TBSTypeEnumEntity.HARDWARE && qty > 0;
    const isSelectionLimitReached: boolean = isSkinSelectionLimited && selectedCount >= qty;

    const fetchOrderDDL = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const resp = await APIGetTBSOrderDetailsDDl();
            if (!resp || !resp.data || resp.status !== 200) {
                return;
            }
            const apiResp: APIResponseEntity = resp.data;
            if (
                apiResp.status &&
                apiResp.statusCode === 200 &&
                apiResp.data &&
                apiResp.data.TBSOrderDetails
            ) {
                setOrderOptions(apiResp.data.TBSOrderDetails);
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, {
                    ...toastOptObj,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchOrderDDL();
    }, []);
    const showError = () => {
        if (!isPendingSignSubmitted || selectedOrderOption) return null;

        return (
            <small className="text-danger font-weight-500 p-1">
                Please select pending skin!
            </small>
        );
    };

    const handleAddPendingSign = async () => {
        setIsPendingSignSubmitted(true);

        if (!selectedOrderOption) return;

        const alreadyAdded = pendingSigns.some(
            (x) =>
                x.title ===
                `${selectedOrderOption.orderNumber} - ${selectedOrderOption.projectName}`
        );

        if (alreadyAdded) {
            toast.error("This order has already been added.", toastOptObj);
            return;
        }

        try {
            setIsLoading(true);

            const resp = await APIGetTbsOrderSolution(
                selectedOrderOption.orderId
            );

            if (!resp || !resp.data || resp.status !== 200) {
                return;
            }

            const apiResp: APIResponseEntity = resp.data;

            if (
                apiResp &&
                apiResp.status &&
                apiResp.statusCode === 200 &&
                apiResp.data
            ) {
                const locations: PendingSignLocation[] =
                    apiResp.data.allSolutionList.map((item: TBSOrderSolutionEntity) => ({
                        id: item.solutionId,
                        locationNo: item.solutionNo.toString(),
                        category: item.category,
                        system: {
                            wall: item.systemsArr[0],
                            flush: item.systemsArr[1],
                            fireRated: item.systemsArr[2],
                            direction: item.systemsArr[3],
                            code: item.csCode,
                            dropSeal: item.systemsArr[5] ?? "",
                            handle: item.systemsArr[6] ?? "",
                            doorCloser: item.systemsArr[7] ?? "",
                        },
                        specs: {
                            location: item.floor,
                            size: item.specsArr[2],
                            fireRating: item.specsArr[3],
                            frame: item.specsArr[4] ?? "",
                            glassOut: item.specsArr[5] ?? "",
                            glassIn: item.specsArr[6] ?? "",
                        },
                        inside: false,
                        outside: false,
                        handle: false,
                        showInside: item.TBSglassOneIn,
                        showOutside: item.TBSglassTwoOut,
                        showHandle: item.TBSHandle,
                        heightRestricted: isHeightRestricted(item),
                    }));
                setPendingSigns((prev) => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        orderId: selectedOrderOption.orderId,
                        orderRefNumber: selectedOrderOption.orderNumber,
                        title: `${selectedOrderOption.orderNumber} - ${selectedOrderOption.projectName}`,
                        locations: locations,
                    },
                ]);

                setSelectedOrderOption(null);
                setIsPendingSignSubmitted(false);
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, {
                    ...toastOptObj,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (id: string) => {
        setPendingSigns((prev) =>
            prev.filter((x) => x.id !== id)
        );
    };

    const handleCheckboxChange = (
        accordionId: string,
        rowId: string,
        field: "inside" | "outside" | "handle",
        value: boolean
    ) => {
        setPendingSigns((prev) => {
            // a skin TBS has only as many items to select as its quantity: the box that would go over is ignored
            if (value && isSkinSelectionLimited && countSelected(prev) >= qty) {
                return prev;
            }

            const updated = prev.map((accordion) => {
                if (accordion.id !== accordionId) return accordion;

                return {
                    ...accordion,
                    locations: accordion.locations.map((row) =>
                        row.id === rowId
                            ? {
                                ...row,
                                [field]: value,
                            }
                            : row
                    ),
                };
            });

            if (isSaveSubmitted) {
                const selectedCount = updated.reduce((count, order) => {
                    return (
                        count +
                        order.locations.reduce((locationCount, location) => {
                            return (
                                locationCount +
                                (location.inside ? 1 : 0) +
                                (location.outside ? 1 : 0) +
                                (location.handle ? 1 : 0)
                            );
                        }, 0)
                    );
                }, 0);

                if (selectedCount === qty) {
                    setValidationMessage("");
                } else {
                    setValidationMessage(
                        `Please select exactly ${qty} ${qty === 1 ? "Item" : "Items"}. Currently selected: ${selectedCount}.`
                    );
                }
            }

            return updated;
        });
    };

    const handleSubmit = async () => {
        setIsSaveSubmitted(true);
        const selectedCount = pendingSigns.reduce((count, order) => {
            return (
                count +
                order.locations.reduce((locationCount, location) => {
                    return (
                        locationCount +
                        (location.inside ? 1 : 0) +
                        (location.outside ? 1 : 0) +
                        (location.handle ? 1 : 0)
                    );
                }, 0)
            );
        }, 0);

        if (selectedCount !== qty) {
            setValidationMessage(
                `Please select exactly ${qty} ${qty === 1 ? "Item" : "Items"}. Currently selected: ${selectedCount}.`
            );
            setTimeout(() => {
                const validationElement = document.querySelector(
                    '[data-error="validation-message"]'
                );

                if (validationElement) {
                    validationElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 100);

            return;
        }
        setValidationMessage("");
        const reqBody: SaveAssignLocationsReqEntity = {
            tbsId: id!,
            locations: pendingSigns.map((order) => ({
                orderId: order.orderId,
                orderRefNumber: order.orderRefNumber,
                solutions: order.locations.map((location) => ({
                    solutionId: location.id,
                    solutionNo: location.locationNo,
                    inside: location.inside,
                    outSide: location.outside,
                    handle: location.handle,
                })),
            })),
        };

        console.log(reqBody);

        const resp = await APISaveAssignLocations(reqBody);
        if (!resp || !resp.data || resp.status !== 200) return;

        const axiosResp: APIResponseEntity = resp.data;

        if (axiosResp.statusCode !== 200 || !axiosResp.data) {
            toast.error(axiosResp.message || axiosResp.errorMsg, {
                ...toastOptObj,
            });
            return;
        }
        onNext();
    };

    useEffect(() => {
        if (!locations || locations.length === 0) return;

        const mappedData: PendingSignData[] = locations.map((order: GetTBSLocationEntity) => ({
            id: order.orderDetails.orderId,
            orderId: order.orderDetails.orderId,
            orderRefNumber: order.orderDetails.orderNumber,
            title: `${order.orderDetails.orderNumber} - ${order.orderDetails.projectName}`,
            locations: order.allSolutionList.map((item: TBSOrderSolutionEntity) => ({
                id: item.solutionId,
                locationNo: item.solutionNo.toString(),
                category: item.category,
                system: {
                    wall: item.systemsArr[0],
                    flush: item.systemsArr[1],
                    fireRated: item.systemsArr[2],
                    direction: item.systemsArr[3],
                    code: item.csCode,
                    dropSeal: item.systemsArr[5] ?? "",
                    handle: item.systemsArr[6] ?? "",
                    doorCloser: item.systemsArr[7] ?? "",
                },
                specs: {
                    location: item.floor,
                    size: item.specsArr[2],
                    fireRating: item.specsArr[3],
                    frame: item.specsArr[4] ?? "",
                    glassOut: item.specsArr[5] ?? "",
                    glassIn: item.specsArr[6] ?? "",
                },
                inside: item.inside,
                outside: item.outside,
                handle: item.handle,
                showInside: item.TBSglassOneIn,
                showOutside: item.TBSglassTwoOut,
                showHandle: item.TBSHandle,
                heightRestricted: isHeightRestricted(item),
            })),
        }));

        setPendingSigns(mappedData);
    }, [locations, materialHeight]);
    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="card mb-3">
                <div className="card-body">
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group mb-0">
                                <label>
                                    PENDING SKINS
                                    <sup className="mandatory">*</sup>
                                </label>
                                {isSkinSelectionLimited && (
                                    <small className="text-muted d-block mt-1">
                                        Selected {selectedCount} of {qty}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: "200px" }}>
                                <Dropdown
                                    value={selectedOrderOption}
                                    options={orderOptions}
                                    placeholder="Select Project"
                                    className="w-100"
                                    filter
                                    filterBy="orderNumber,projectName"
                                    filterPlaceholder="Search Order..."
                                    onChange={(e) => setSelectedOrderOption(e.value)}
                                    valueTemplate={(option: OrderOption) =>
                                        option
                                            ? `${option.orderNumber} - ${option.projectName}`
                                            : "Select Project"
                                    }
                                    itemTemplate={(option: OrderOption) => (
                                        <span>
                                            {option.orderNumber} - {option.projectName}
                                        </span>
                                    )}
                                />
                            </div>

                            <button
                                type="button"
                                className="btn btn-success px-4"
                                onClick={handleAddPendingSign}
                            >
                                Add
                            </button>
                        </div>

                        {validationMessage && (
                            <div
                                data-error="validation-message"
                                className="ms-3"
                                style={{ maxWidth: "450px" }}
                            >
                                <Message
                                    className="w-100 justify-content-start"
                                    severity="info"
                                    content={
                                        <div className="d-flex align-items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                fill="currentColor"
                                                className="me-2 flex-shrink-0"
                                                viewBox="0 0 16 16"
                                            >
                                                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2" />
                                            </svg>

                                            <small className="mb-0">
                                                {validationMessage}
                                            </small>
                                        </div>
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {showError()}
                </div>
            </div>
            <div className="mt-4">
                {pendingSigns.map((item) => (
                    <PendingSignsAccordion
                        key={item.id}
                        title={item.title}
                        locations={item.locations}
                        onDelete={() => handleDelete(item.id)}
                        isSelectionLimitReached={isSelectionLimitReached}
                        selectionLimitNote={`Only ${qty} ${qty === 1 ? "item" : "items"} can be selected for this TBS`}
                        onCheckboxChange={(rowId, field, value) =>
                            handleCheckboxChange(
                                item.id,
                                rowId,
                                field,
                                value
                            )
                        }
                    />
                ))}
            </div>

            <div className="row mt-3">
                <div className="col-md-12 d-flex justify-content-between">

                    <button
                        type="button"
                        className="btn btn-primary btn-md"
                        onClick={onBack}
                    >
                        Previous
                    </button>

                    <button
                        type="button"
                        className="btn btn-success btn-md"
                        onClick={handleSubmit}
                    >
                        Save & Next
                    </button>

                </div>
            </div>
        </>
    );
};

export default AssignLocationsStep;